import React, { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { DepButton, DepButtonDisplay } from '@pkg/components/deprecated';
import { use2FA, useAppDispatch, useAppSelector, useFormattedTime, useLogout } from '@pkg/hooks';
import { LocalStorageAuthFlowValues, LocalStorageKeys, TwoFactorAuthType } from '@pkg/types';
import { handleKeyDown } from '@pkg/utils';

import { apiFactory, Auth } from '@app/client';
import config from '@app/config';
import { YAT_PICKER_CHECKOUT } from '@app/routes/paths';
import { setError } from '@app/store/actions/error';
import { set2faResetCode } from '@app/store/slices';
import { useAuthTokens } from '@app/store/zustand/auth/useAuthTokens';

import SecurityCodeInput, { SecurityCodeInputVariants } from '../Input/SecurityCodeInput';

import './_LoginForm2FA.scss';

const RESEND_TIMEOUT = 120;
const { twoFACodeDigitCount } = config;

interface YatLoginForm2FAProps {
    on2faSubmit?: (loading?) => void;
}

const YatLoginForm2FA = ({ on2faSubmit }: YatLoginForm2FAProps) => {
    const { t } = useTranslation(['account', 'translation', 'common']);
    const tokens = useAuthTokens((rootState) => rootState.token);
    const authUser = useAppSelector((state) => state.user);
    const [authCodes, setAuthCodes] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);
    const [remainingSeconds, setRemainingSeconds] = useState(0);

    const dispatch = useAppDispatch();
    const { login2FA } = use2FA();

    const formattedTime = useFormattedTime(remainingSeconds);

    const onResend = useCallback(() => {
        apiFactory()
            .call(Auth.sendSMS, {})
            .then((res) => {
                if (!res.ok) {
                    dispatch(setError({ ...res, notify: true }));
                    return;
                }
                dispatch(set2faResetCode(true));
            })
            .finally(() => {
                setRemainingSeconds(RESEND_TIMEOUT);
            });
    }, [dispatch]);

    const userAccount = authUser?.userAccount;

    const twoFactorAuth = useMemo(() => {
        return userAccount?.user.two_factor_auth;
    }, [userAccount]);

    const isSMS2FA = useMemo(() => {
        return (twoFactorAuth || []).includes(TwoFactorAuthType.SMS);
    }, [twoFactorAuth]);

    const submitCb = useCallback(
        (tokens) => {
            if (!tokens) {
                on2faSubmit?.(false);
                dispatch(set2faResetCode(true));
            } else {
                on2faSubmit?.(true);
            }
            setIsLoggingIn(tokens);
        },
        [on2faSubmit, dispatch],
    );

    const handleSubmit = useCallback(
        async (event?: FormEvent) => {
            setIsLoggingIn(true);
            event?.preventDefault();
            if (!tokens) {
                return;
            }

            const currentAuthFlow = localStorage.getItem(LocalStorageKeys.AuthFlow);
            const redirect = currentAuthFlow === LocalStorageAuthFlowValues.Checkout ? YAT_PICKER_CHECKOUT : undefined;

            const twoFAResponse = await login2FA({ code: authCodes, refreshToken: tokens.refresh_token, redirect });
            if (twoFAResponse && twoFAResponse.ok) {
                submitCb(twoFAResponse);
            } else {
                setIsLoggingIn(false);
            }
        },
        [tokens, login2FA, authCodes, submitCb],
    );

    useEffect(() => {
        if (isSMS2FA) {
            onResend();
        }
    }, [onResend, isSMS2FA]);

    const { logout } = useLogout();

    return (
        <div className="two-fa-login-form">
            <form className={'two-fa-login-form__form'} onSubmit={handleSubmit}>
                <div>
                    <SecurityCodeInput
                        setCompleteCode={(code) => setAuthCodes(code)}
                        digitCount={twoFACodeDigitCount}
                        classes="two-fa-login-form__input--2fa"
                        variant={SecurityCodeInputVariants.MidSpaced}
                        digitOnly
                        onAutoSubmit={handleSubmit}
                    />
                </div>
                <div className="user-account-page__btn">
                    <DepButton
                        disabled={authCodes.length < twoFACodeDigitCount}
                        loading={isLoggingIn}
                        display={DepButtonDisplay.minWidth}
                        variant="large"
                        type={'submit'}
                    >
                        {t('common:login')}
                    </DepButton>
                </div>
                {isSMS2FA &&
                    (remainingSeconds === 0 ? (
                        <p
                            tabIndex={0}
                            className="two-fa-login-form__send"
                            onClick={onResend}
                            onKeyDown={(e) => handleKeyDown(e, ['Enter', ' '], onResend)}
                        >
                            {t('twoFactorAuth:2fa_step_setup_authenticator_enter_resend')}
                        </p>
                    ) : (
                        <p className="two-fa-login-form__send">
                            {t('twoFactorAuth:2fa_step_setup_authenticator_enter_resend_in', {
                                timeString: formattedTime,
                            })}
                        </p>
                    ))}
                <p
                    tabIndex={0}
                    className="two-fa-login-form__send"
                    onClick={() => logout()}
                    onKeyDown={(e) => handleKeyDown(e, ['Enter', ' '], logout)}
                >
                    {t('twoFactorAuth:2fa_another_account')}
                </p>
            </form>
        </div>
    );
};

export default YatLoginForm2FA;
